import { useEffect, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { Plus, Send, FileSpreadsheet, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const rupee = (v) => "₹ " + Number(v||0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const COLORS = ["#B35C44", "#597658", "#C89B3C", "#8B3A3A", "#5C5C5C"];

function PnL() {
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState({ date: "", category: "general", amount: "", note: "", mode: "cash" });

  const load = async () => { const { data } = await api.get("/reports/pnl", { params: { period } }); setD(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);
  const addExpense = async () => {
    try { await api.post("/expenses", { ...exp, amount: Number(exp.amount) }); toast.success("Expense added"); setOpen(false); setExp({ date:"", category:"general", amount:"", note:"", mode:"cash"}); load(); }
    catch { toast.error("Failed"); }
  };

  if (!d) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="pnl-period"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Today</SelectItem>
            <SelectItem value="month">This month</SelectItem>
            <SelectItem value="year">This year</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-[#984D38]" data-testid="add-expense-btn"><Plus size={14} className="mr-2"/>Add Expense</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">Log Expense</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Date</Label><Input type="date" value={exp.date} onChange={e=>setExp({...exp,date:e.target.value})}/></div>
              <div><Label>Category</Label><Input value={exp.category} onChange={e=>setExp({...exp,category:e.target.value})} placeholder="e.g. rent, salary, materials" data-testid="exp-category"/></div>
              <div><Label>Amount (₹)</Label><Input type="number" value={exp.amount} onChange={e=>setExp({...exp,amount:e.target.value})} data-testid="exp-amount"/></div>
              <div><Label>Mode</Label>
                <Select value={exp.mode} onValueChange={v=>setExp({...exp, mode:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Note</Label><Textarea rows={2} value={exp.note} onChange={e=>setExp({...exp,note:e.target.value})}/></div>
            </div>
            <DialogFooter><Button onClick={addExpense} className="bg-primary hover:bg-[#984D38]" data-testid="exp-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="p-5 bg-white border-border"><p className="text-xs uppercase text-muted-foreground">Sales</p><p className="font-serif-elegant text-3xl mt-2">{rupee(d.sales)}</p></Card>
        <Card className="p-5 bg-white border-border"><p className="text-xs uppercase text-muted-foreground">Tax collected</p><p className="font-serif-elegant text-3xl mt-2">{rupee(d.tax)}</p></Card>
        <Card className="p-5 bg-white border-border"><p className="text-xs uppercase text-muted-foreground">Expenses</p><p className="font-serif-elegant text-3xl mt-2">{rupee(d.expenses)}</p></Card>
        <Card className="p-5 bg-white border-border"><p className="text-xs uppercase text-muted-foreground">Net profit</p><p className="font-serif-elegant text-3xl mt-2 text-primary">{rupee(d.net_profit)}</p></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 bg-white border-border lg:col-span-2">
          <h3 className="font-serif-elegant text-2xl mb-3">Sales vs Expenses</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.buckets}>
                <XAxis dataKey="label" stroke="#5C5C5C" fontSize={11}/>
                <YAxis stroke="#5C5C5C" fontSize={11}/>
                <Tooltip formatter={(v)=>rupee(v)} contentStyle={{ background: "#fff", border: "1px solid #EBE8E0", borderRadius: 8 }}/>
                <Legend/>
                <Bar dataKey="sales" fill="#B35C44" radius={[4,4,0,0]}/>
                <Bar dataKey="expenses" fill="#8B3A3A" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-6 bg-white border-border">
          <h3 className="font-serif-elegant text-2xl mb-3">Expenses by Category</h3>
          {d.expenses_by_category.length===0 ? <p className="text-sm text-muted-foreground">No expenses recorded.</p> : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.expenses_by_category} dataKey="total" nameKey="category" outerRadius={80} label={(e)=>e.category}>
                    {d.expenses_by_category.map((_, i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={(v)=>rupee(v)}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Outstanding() {
  const [d, setD] = useState(null);
  const load = async () => { const { data } = await api.get("/reports/outstanding"); setD(data); };
  useEffect(() => { load(); }, []);
  const sendReminders = async () => {
    try { const { data } = await api.post("/reminders/run"); toast.success(`Sent ${data.sent} reminders`); load(); }
    catch { toast.error("Admin only"); }
  };
  if (!d) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="grid grid-cols-4 gap-3 flex-1">
          {d.buckets.map(b => (
            <Card key={b.bucket} className="p-4 bg-white border-border">
              <p className="text-xs uppercase text-muted-foreground">{b.bucket} days</p>
              <p className="font-serif-elegant text-2xl mt-2">{rupee(b.amount)}</p>
            </Card>
          ))}
        </div>
        <Button onClick={sendReminders} className="bg-primary hover:bg-[#984D38]" data-testid="run-reminders-btn"><Send size={14} className="mr-2"/>Send WhatsApp reminders</Button>
      </div>
      <Card className="border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-secondary/50">
            <tr>
              <th className="py-3 px-4">Invoice</th>
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-4">Due</th>
              <th className="py-3 px-4">Days</th>
              <th className="py-3 px-4 text-right">Total</th>
              <th className="py-3 px-4 text-right">Paid</th>
              <th className="py-3 px-4 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.length===0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">🎉 No outstanding invoices.</td></tr>}
            {d.rows.map(r => (
              <tr key={r.invoice_id} className="border-t hairline">
                <td className="py-3 px-4 font-medium">{r.number}</td>
                <td className="py-3 px-4">{r.customer}</td>
                <td className="py-3 px-4 text-muted-foreground">{r.due_date}</td>
                <td className="py-3 px-4"><Badge variant="outline" className={r.days_overdue>30?"bg-red-100 text-red-800 border-red-200":"bg-amber-100 text-amber-800 border-amber-200"}>{r.days_overdue}d</Badge></td>
                <td className="py-3 px-4 text-right">{rupee(r.total)}</td>
                <td className="py-3 px-4 text-right">{rupee(r.paid)}</td>
                <td className="py-3 px-4 text-right font-semibold">{rupee(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Gst() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const dl = async (path, fname) => {
    const token = localStorage.getItem("gaze_token");
    const r = await fetch(`${BACKEND}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error("Failed");
    const b = await r.blob(); const url = URL.createObjectURL(b);
    const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url); toast.success("Downloaded");
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="p-6 bg-white border-border">
        <div className="flex items-center gap-2 mb-1"><FileSpreadsheet size={16} className="text-primary"/><h3 className="font-serif-elegant text-2xl">GSTR-1</h3></div>
        <p className="text-sm text-muted-foreground">B2B, B2CS and HSN sheets for the GST portal.</p>
        <div className="flex gap-3 mt-4 items-end">
          <div className="flex-1"><Label>Month</Label><Input type="month" value={month} onChange={e=>setMonth(e.target.value)} data-testid="gstr1-month"/></div>
          <Button onClick={()=>dl(`/api/reports/gstr1?month=${month}`, `GSTR1_${month}.xlsx`)} className="bg-primary hover:bg-[#984D38]" data-testid="gstr1-download"><Download size={14} className="mr-2"/>GSTR-1</Button>
        </div>
      </Card>
      <Card className="p-6 bg-white border-border">
        <div className="flex items-center gap-2 mb-1"><FileSpreadsheet size={16} className="text-primary"/><h3 className="font-serif-elegant text-2xl">GSTR-3B</h3></div>
        <p className="text-sm text-muted-foreground">Monthly summary of outward, ITC and tax payable (cash + carry forward).</p>
        <div className="flex gap-3 mt-4 items-end">
          <div className="flex-1"><Label>Month</Label><Input type="month" value={month} onChange={e=>setMonth(e.target.value)} data-testid="gstr3b-month"/></div>
          <Button onClick={()=>dl(`/api/reports/gstr3b?month=${month}`, `GSTR3B_${month}.xlsx`)} className="bg-primary hover:bg-[#984D38]" data-testid="gstr3b-download"><Download size={14} className="mr-2"/>GSTR-3B</Button>
        </div>
      </Card>
      <Card className="p-6 bg-white border-border">
        <div className="flex items-center gap-2 mb-1"><FileSpreadsheet size={16} className="text-primary"/><h3 className="font-serif-elegant text-2xl">Full Excel Backup</h3></div>
        <p className="text-sm text-muted-foreground">Single workbook with all records. Admin only.</p>
        <Button onClick={()=>dl(`/api/backup/excel`, `GazeDecor_Backup_${new Date().toISOString().slice(0,10)}.xlsx`)} className="bg-primary hover:bg-[#984D38] mt-4" data-testid="backup-download"><Download size={14} className="mr-2"/>Download Backup</Button>
      </Card>
    </div>
  );
}

export default function Reports() {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Books</p>
        <h1 className="font-serif-elegant text-5xl mt-1">Reports</h1>
      </div>
      <Tabs defaultValue="pnl">
        <TabsList>
          <TabsTrigger value="pnl" data-testid="tab-pnl">Profit & Loss</TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="tab-outstanding">Outstanding</TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst">GST · Backup</TabsTrigger>
        </TabsList>
        <TabsContent value="pnl" className="mt-4"><PnL/></TabsContent>
        <TabsContent value="outstanding" className="mt-4"><Outstanding/></TabsContent>
        <TabsContent value="gst" className="mt-4"><Gst/></TabsContent>
      </Tabs>
    </div>
  );
}
